import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TenantLayout from '../components/TenantLayout';
import BulkPricingModal from '../components/BulkPricingModal';
import PricingDashboard from '../components/PricingDashboard';
import PricingStrategies from '../components/PricingStrategies';
import { useSite } from '../context/SiteContext';
import { API_BASE_URL } from '../config/api';

const TenantTickets = () => {
  const { selectedSite, sites, updateTrigger } = useSite();
  const [tickets, setTickets] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [urlSiteFilter, setUrlSiteFilter] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBulkPricing, setShowBulkPricing] = useState(false);
  const [showPricingDashboard, setShowPricingDashboard] = useState(false);
  const [showPricingStrategies, setShowPricingStrategies] = useState(false);
  const [categoryAnalytics, setCategoryAnalytics] = useState(null);
  const [tenantAllowedFeatures, setTenantAllowedFeatures] = useState([]);

  const allCategories = [
    { key: 'all', label: 'All Categories', icon: '🎯' },
    { key: 'entry_ticket', label: 'Entry Tickets', icon: '🌳' },
    { key: 'parking_ticket', label: 'Parking Tickets', icon: '🅿️' },
    { key: 'chappal_stand_ticket', label: 'Chappal Stand', icon: '👟' },
    { key: 'puja_ticket', label: 'Puja Services', icon: '🙏' },
    { key: 'photo_shoot_ticket', label: 'Photo Shoots', icon: '📸' },
    { key: 'function_hall_ticket', label: 'Function Hall', icon: '🎉' },
    { key: 'event_ticket', label: 'Events', icon: '🎭' },
    { key: 'pass_ticket', label: 'Pass Tickets', icon: '📅' },
    { key: 'other', label: 'Other', icon: '🏛️' }
  ];

  // Filter categories based on tenant's allowed features and selected site's features
  const getFilteredCategories = () => {
    let availableCategories = allCategories.slice(1).filter(category => 
      tenantAllowedFeatures.includes(category.key)
    );
    
    // Further filter by site's enabled features if a site is selected
    if (selectedSite && selectedSite !== 'all') {
      const currentSite = sites.find(s => s._id === selectedSite);
      if (currentSite && currentSite.features) {
        availableCategories = availableCategories.filter(category => 
          currentSite.features.includes(category.key)
        );
      }
    }
    
    return [allCategories[0], ...availableCategories]; // Always include 'All Categories'
  };
  
  const categories = getFilteredCategories();

  useEffect(() => {
    // Get site filter from URL
    const urlParams = new URLSearchParams(window.location.search);
    const siteParam = urlParams.get('site');
    setUrlSiteFilter(siteParam);
    
    // Listen for site changes from navbar
    const handleSiteChange = () => {
      fetchTickets();
    };
    window.addEventListener('siteChanged', handleSiteChange);
    return () => window.removeEventListener('siteChanged', handleSiteChange);
  }, []);

  useEffect(() => {
    fetchTenantFeatures();
  }, []);

  useEffect(() => {
    fetchTickets();
    if (selectedCategory !== 'all') {
      fetchCategoryAnalytics();
    }
  }, [selectedCategory, selectedSite, urlSiteFilter, updateTrigger]);

  const fetchTenantFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const currentTenant = response.data.tenants.find(t => t._id === user.tenantId);
      setTenantAllowedFeatures(currentTenant?.allowedFeatures || allCategories.slice(1).map(c => c.key));
    } catch (error) {
      console.error('Error fetching tenant features:', error);
      setTenantAllowedFeatures(allCategories.slice(1).map(c => c.key));
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API_BASE_URL}/api/tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': user.tenantId
        }
      });
      let filteredTickets = response.data.tickets || [];
      
      // Filter by selected site from context (top navbar)
      console.log('TenantTickets filtering - selectedSite from context:', selectedSite, 'urlSiteFilter:', urlSiteFilter);
      if (selectedSite && selectedSite !== 'all') {
        const originalCount = filteredTickets.length;
        filteredTickets = filteredTickets.filter(ticket => {
          console.log('Checking ticket:', ticket.name, 'siteId:', ticket.siteId, 'vs selectedSite:', selectedSite);
          return ticket.siteId === selectedSite;
        });
        console.log(`Site filter applied: ${originalCount} -> ${filteredTickets.length} tickets (filter: ${selectedSite})`);
      } else if (urlSiteFilter && urlSiteFilter !== 'all') {
        const originalCount = filteredTickets.length;
        filteredTickets = filteredTickets.filter(ticket => {
          console.log('Checking ticket:', ticket.name, 'siteId:', ticket.siteId, 'vs urlSiteFilter:', urlSiteFilter);
          return ticket.siteId === urlSiteFilter;
        });
        console.log(`URL Site filter applied: ${originalCount} -> ${filteredTickets.length} tickets (filter: ${urlSiteFilter})`);
      }
      
      // Filter by category
      if (selectedCategory !== 'all') {
        filteredTickets = filteredTickets.filter(ticket => ticket.category === selectedCategory);
      }
      
      setTickets(filteredTickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryAnalytics = async () => {
    if (selectedCategory === 'all') return;
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API_BASE_URL}/api/tickets/category/${selectedCategory}/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': user.tenantId
        }
      });
      setCategoryAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  return (
    <TenantLayout>
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Ticket Management</h1>
          {(urlSiteFilter || (selectedSite && selectedSite !== 'all')) && (
            <div style={{ 
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              display: 'inline-block'
            }}>
              🏛️ Filtered by site: {urlSiteFilter || selectedSite} ({tickets.length} tickets)
            </div>
          )}
          {(!urlSiteFilter && (!selectedSite || selectedSite === 'all')) && (
            <p style={{ color: '#718096', margin: 0 }}>Showing all tickets ({tickets.length} total)</p>
          )}
        </div>

        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '30px', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              style={{
                padding: '10px 20px',
                borderRadius: '25px',
                border: 'none',
                background: selectedCategory === cat.key ? 'linear-gradient(45deg, #14b8a6, #0d9488)' : 'white',
                color: selectedCategory === cat.key ? 'white' : '#4a5568',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{cat.icon}</span>
              {cat.label}
            </button>
          ))}
        </div>
        
        {(selectedSite && selectedSite !== 'all') && (
          <div style={{
            background: 'linear-gradient(45deg, #06b6d4, #10b981)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            📍 Showing categories available for selected site only
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: 'linear-gradient(45deg, #14b8a6, #0d9488)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(20, 184, 166, 0.3)'
            }}
          >
            ➕ Add New Ticket
          </button>
          
          {selectedCategory !== 'all' && tickets.length > 0 && (
            <button
              onClick={() => setShowBulkPricing(true)}
              style={{
                background: 'linear-gradient(45deg, #059669, #047857)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(5, 150, 105, 0.3)'
              }}
            >
              💰 Bulk Price Update
            </button>
          )}
          
          <button
            onClick={() => setShowPricingDashboard(true)}
            style={{
              background: 'linear-gradient(45deg, #0891b2, #0e7490)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(8, 145, 178, 0.3)'
            }}
          >
            📊 Pricing Dashboard
          </button>
          
          <button
            onClick={() => setShowPricingStrategies(true)}
            style={{
              background: 'linear-gradient(45deg, #10b981, #059669)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
            }}
          >
            🎯 Pricing Strategies
          </button>
        </div>

        {/* Category Analytics */}
        {categoryAnalytics && selectedCategory !== 'all' && (
          <div style={{
            background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            boxShadow: '0 4px 15px rgba(20, 184, 166, 0.3)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {categories.find(c => c.key === selectedCategory)?.icon} 
              {categories.find(c => c.key === selectedCategory)?.label} Analytics
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>{categoryAnalytics.totalTickets}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Tickets</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>₹{categoryAnalytics.averagePrice.toFixed(0)}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Average Price</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>₹{categoryAnalytics.priceRange.min}-{categoryAnalytics.priceRange.max}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Price Range</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>{categoryAnalytics.cities.length}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Cities</div>
              </div>
            </div>
          </div>
        )}

        {/* Tickets Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px' }}>⏳ Loading tickets...</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {tickets.map(ticket => {
              const categoryInfo = categories.find(cat => cat.key === ticket.category) || { icon: '🎫', label: 'Unknown' };
              return (
                <div key={ticket._id} style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #e2e8f0',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '24px' }}>{categoryInfo.icon}</span>
                    <div>
                      <h3 style={{ margin: '0', color: '#2d3748', fontSize: '18px', fontWeight: '700' }}>{ticket.name}</h3>
                      <p style={{ margin: '4px 0 0 0', color: '#718096', fontSize: '14px' }}>{categoryInfo.label}</p>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ color: '#718096', margin: '4px 0', fontSize: '14px' }}>📍 {ticket.city}</p>
                    <p style={{ color: '#718096', margin: '4px 0', fontSize: '14px' }}>📝 {ticket.description || 'No description'}</p>
                  </div>
                  
                  <div style={{ 
                    background: 'linear-gradient(45deg, #10b981, #059669)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>₹{ticket.price}</div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>Per Ticket</div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => setEditingTicket(ticket)}
                      style={{
                        flex: 1,
                        background: '#0891b2',
                        color: 'white',
                        border: 'none',
                        padding: '10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}>
                      ✏️ Edit
                    </button>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this ticket?')) {
                          try {
                            const token = localStorage.getItem('token');
                            const user = JSON.parse(localStorage.getItem('user') || '{}');
                            await axios.delete(`${API_BASE_URL}/api/tickets/${ticket._id}`, {
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'x-tenant-id': user.tenantId
                              }
                            });
                            fetchTickets();
                          } catch (error) {
                            alert('Error deleting ticket');
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && tickets.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '16px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>
              {selectedCategory === 'all' ? '🎫' : categories.find(c => c.key === selectedCategory)?.icon || '🎫'}
            </div>
            <h3 style={{ color: '#2d3748', marginBottom: '8px' }}>No tickets found</h3>
            <p style={{ color: '#718096', marginBottom: '24px' }}>
              {selectedCategory === 'all' 
                ? 'No tickets have been created yet' 
                : `No ${categories.find(c => c.key === selectedCategory)?.label.toLowerCase()} found`}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                background: '#14b8a6',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Create First Ticket
            </button>
          </div>
        )}
        
        {/* Create/Edit Ticket Modal */}
        {(showCreateModal || editingTicket) && (
          <TicketModal
            ticket={editingTicket}
            categories={categories}
            onClose={() => {
              setShowCreateModal(false);
              setEditingTicket(null);
            }}
            onSave={() => {
              fetchTickets();
              fetchCategoryAnalytics();
              setShowCreateModal(false);
              setEditingTicket(null);
            }}
          />
        )}

        {/* Bulk Pricing Modal */}
        {showBulkPricing && (
          <BulkPricingModal
            category={selectedCategory}
            categoryInfo={categories.find(c => c.key === selectedCategory)}
            onClose={() => setShowBulkPricing(false)}
            onSuccess={(message) => {
              alert(message);
              fetchTickets();
              fetchCategoryAnalytics();
            }}
          />
        )}

        {/* Pricing Dashboard */}
        {showPricingDashboard && (
          <PricingDashboard
            onClose={() => setShowPricingDashboard(false)}
          />
        )}

        {/* Pricing Strategies */}
        {showPricingStrategies && (
          <PricingStrategies
            onClose={() => setShowPricingStrategies(false)}
            onSuccess={(message) => {
              alert(message);
              fetchTickets();
              fetchCategoryAnalytics();
            }}
          />
        )}
      </div>
    </TenantLayout>
  );
};

// Ticket Modal Component
const TicketModal = ({ ticket, categories, onClose, onSave }) => {
  const { selectedSite, sites } = useSite();
  const [formData, setFormData] = useState({
    name: ticket?.name || '',
    description: ticket?.description || '',
    category: ticket?.category || 'entry_ticket',
    price: ticket?.price || '',
    city: ticket?.city || '',
    siteId: ticket?.siteId || '',
    startTime: ticket?.schedule?.startTime || '09:00',
    endTime: ticket?.schedule?.endTime || '18:00',
    // Entry ticket fields
    ageLimit: ticket?.ageLimit || 12,
    groupDiscount: ticket?.groupDiscount || 0,
    // Pass ticket settings
    passTicketSettings: ticket?.passTicketSettings || {
      requiresPhoto: false,
      requiresIdProof: false,
      passType: 'unlimited',
      visitLimit: 10
    },
    // Parking fields
    vehicleType: ticket?.vehicleType || 'car',
    parkingDuration: ticket?.parkingDuration || 24,
    // Event fields
    eventDate: ticket?.eventDate || '',
    capacity: ticket?.capacity || 100,
    // Puja services
    pujaServices: ticket?.pujaServices || {
      'Morning Aarti': { enabled: true, price: 50 },
      'Evening Aarti': { enabled: true, price: 50 },
      'Abhishek': { enabled: true, price: 100 },
      'Prasad': { enabled: true, price: 25 },
      'Flower Offering': { enabled: true, price: 30 },
      'Coconut Offering': { enabled: true, price: 20 },
      'Incense Sticks': { enabled: true, price: 15 },
      'Special Puja': { enabled: true, price: 200 }
    },
    // Parking settings
    parkingSettings: ticket?.parkingSettings || {
      'car': { enabled: true, price: 50, duration: 24 },
      'motorcycle': { enabled: true, price: 25, duration: 24 },
      'bus': { enabled: true, price: 150, duration: 24 },
      'truck': { enabled: true, price: 125, duration: 24 }
    }
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const ticketData = {
        ...formData,
        price: formData.category === 'puja_ticket' ? 0 : parseFloat(formData.price),
        siteId: selectedSite && selectedSite !== 'all' ? selectedSite : formData.siteId,

        schedule: {
          startTime: formData.startTime,
          endTime: formData.endTime,
          availableDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        }
      };

      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-tenant-id': user.tenantId
      };

      if (ticket) {
        // Update existing ticket
        await axios.put(`${API_BASE_URL}/api/tickets/${ticket._id}`, ticketData, { headers });
      } else {
        // Create new ticket
        await axios.post(`${API_BASE_URL}/api/tickets`, ticketData, { headers });
      }
      
      onSave();
    } catch (error) {
      console.error('Error saving ticket:', error);
      alert('Error saving ticket. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const categoryInfo = categories.find(cat => cat.key === formData.category) || { icon: '🎫', label: 'Unknown' };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <span style={{ fontSize: '32px' }}>{categoryInfo.icon}</span>
          <h2 style={{ margin: 0, color: '#2d3748' }}>
            {ticket ? 'Edit Ticket' : 'Create New Ticket'}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Ticket Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              placeholder="Enter ticket name"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
            >
              {categories.slice(1).map(cat => (
                <option key={cat.key} value={cat.key}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Price (₹)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                required={formData.category !== 'puja_ticket'}
                min="0"
                step="0.01"
                disabled={formData.category === 'puja_ticket'}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  opacity: formData.category === 'puja_ticket' ? 0.5 : 1
                }}
                placeholder={formData.category === 'puja_ticket' ? 'Calculated from services' : '0.00'}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
                placeholder="City name"
              />
            </div>
          </div>

          {(selectedSite === 'all' || !selectedSite || ticket) && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Site {ticket ? '(Reassign)' : '(Optional)'}</label>
              <select
                value={formData.siteId}
                onChange={(e) => setFormData({...formData, siteId: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              >
                <option value="">-- No specific site --</option>
                {sites.map(site => (
                  <option key={site._id} value={site._id}>
                    {site.name} - {site.city}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                resize: 'vertical'
              }}
              placeholder="Describe the ticket..."
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Start Time</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>End Time</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Category-specific fields */}
          {formData.category === 'entry_ticket' && (
            <div style={{ marginBottom: '24px', padding: '20px', background: '#f0fdf4', borderRadius: '12px', border: '2px solid #22c55e' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#15803d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🌳 Entry Ticket Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Child Age Limit</label>
                  <input
                    type="number"
                    value={formData.ageLimit}
                    onChange={(e) => setFormData({...formData, ageLimit: parseInt(e.target.value) || 12})}
                    min="0"
                    max="18"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Group Discount (%)</label>
                  <input
                    type="number"
                    value={formData.groupDiscount}
                    onChange={(e) => setFormData({...formData, groupDiscount: parseInt(e.target.value) || 0})}
                    min="0"
                    max="50"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {formData.category === 'parking_ticket' && (
            <ParkingSettingsEditor 
              settings={formData.parkingSettings}
              onChange={(settings) => setFormData({...formData, parkingSettings: settings})}
            />
          )}

          {formData.category === 'event_ticket' && (
            <div style={{ marginBottom: '24px', padding: '20px', background: '#fdf2f8', borderRadius: '12px', border: '2px solid #ec4899' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#be185d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🎭 Event Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Event Date</label>
                  <input
                    type="date"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Capacity</label>
                  <input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || 100})}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {formData.category === 'puja_ticket' && (
            <PujaServicesEditor 
              services={formData.pujaServices}
              onChange={(services) => setFormData({...formData, pujaServices: services})}
            />
          )}

          {formData.category === 'function_hall_ticket' && (
            <FunctionHallEditor 
              settings={formData.functionHallSettings}
              onChange={(settings) => setFormData({...formData, functionHallSettings: settings})}
            />
          )}

          {formData.category === 'pass_ticket' && (
            <div style={{ marginBottom: '24px', padding: '20px', background: '#f0f9ff', borderRadius: '12px', border: '2px solid #3b82f6' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📅 Pass Ticket Settings
              </h3>
              
              {/* Pass Pricing Options */}
              <div style={{ marginBottom: '20px', padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '16px' }}>Pass Pricing Options</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  {['monthly', 'quarterly', 'halfYearly', 'yearly'].map(period => {
                    const labels = {
                      monthly: 'Monthly (30 days)',
                      quarterly: 'Quarterly (90 days)',
                      halfYearly: 'Half Yearly (180 days)',
                      yearly: 'Yearly (365 days)'
                    };
                    return (
                      <div key={period} style={{ padding: '12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                          {labels[period]}
                        </label>
                        <input
                          type="number"
                          placeholder="Price (₹)"
                          value={formData.passTicketSettings?.passPricing?.[period]?.price || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            passTicketSettings: {
                              ...formData.passTicketSettings,
                              passPricing: {
                                ...formData.passTicketSettings?.passPricing,
                                [period]: {
                                  price: parseFloat(e.target.value) || 0,
                                  validityDays: period === 'monthly' ? 30 : period === 'quarterly' ? 90 : period === 'halfYearly' ? 180 : 365
                                }
                              }
                            }
                          })}
                          min="0"
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Pass Type</label>
                  <select
                    value={formData.passTicketSettings?.passType || 'unlimited'}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      passTicketSettings: {
                        ...formData.passTicketSettings,
                        passType: e.target.value
                      }
                    })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="unlimited">Unlimited Visits</option>
                    <option value="limited">Limited Visits</option>
                  </select>
                </div>
                <div style={{ opacity: 0.5 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Base Price (₹)</label>
                  <input
                    type="number"
                    value={formData.price}
                    disabled
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#f9fafb'
                    }}
                    placeholder="Set pricing options above"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>Use pricing options above</small>
                </div>
              </div>
              
              {formData.passTicketSettings?.passType === 'limited' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Visit Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.passTicketSettings?.visitLimit || 10}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      passTicketSettings: {
                        ...formData.passTicketSettings,
                        visitLimit: parseInt(e.target.value) || 10
                      }
                    })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>Required Documents</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.passTicketSettings?.requiresPhoto || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        passTicketSettings: {
                          ...formData.passTicketSettings,
                          requiresPhoto: e.target.checked
                        }
                      })}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>📷 Passport Photo</span>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.passTicketSettings?.requiresIdProof || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        passTicketSettings: {
                          ...formData.passTicketSettings,
                          requiresIdProof: e.target.checked
                        }
                      })}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>🆔 ID Proof (Govt ID/School ID)</span>
                  </label>
                </div>
              </div>
              
              {/* Slot Management */}
              <div style={{ marginTop: '16px', padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    checked={formData.passTicketSettings?.slotManagement?.enabled || false}
                    onChange={(e) => setFormData({
                      ...formData,
                      passTicketSettings: {
                        ...formData.passTicketSettings,
                        slotManagement: {
                          ...formData.passTicketSettings?.slotManagement,
                          enabled: e.target.checked,
                          timeSlots: formData.passTicketSettings?.slotManagement?.timeSlots || []
                        }
                      }
                    })}
                    style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }}
                  />
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>⏰ Enable Slot Management</label>
                </div>
                
                {formData.passTicketSettings?.slotManagement?.enabled && (
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', color: '#6b7280' }}>Time Slots</label>
                    </div>
                    {(formData.passTicketSettings?.slotManagement?.timeSlots || []).map((slot, index) => (
                      <div key={index} style={{ display: 'grid', gridTemplateColumns: '120px 120px 100px 80px', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                        <input
                          type="time"
                          value={slot.startTime}
                          onChange={(e) => {
                            const updatedSlots = [...(formData.passTicketSettings?.slotManagement?.timeSlots || [])];
                            updatedSlots[index] = { ...slot, startTime: e.target.value };
                            setFormData({
                              ...formData,
                              passTicketSettings: {
                                ...formData.passTicketSettings,
                                slotManagement: {
                                  ...formData.passTicketSettings?.slotManagement,
                                  timeSlots: updatedSlots
                                }
                              }
                            });
                          }}
                          style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <input
                          type="time"
                          value={slot.endTime}
                          onChange={(e) => {
                            const updatedSlots = [...(formData.passTicketSettings?.slotManagement?.timeSlots || [])];
                            updatedSlots[index] = { ...slot, endTime: e.target.value };
                            setFormData({
                              ...formData,
                              passTicketSettings: {
                                ...formData.passTicketSettings,
                                slotManagement: {
                                  ...formData.passTicketSettings?.slotManagement,
                                  timeSlots: updatedSlots
                                }
                              }
                            });
                          }}
                          style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <input
                          type="number"
                          placeholder="Max Capacity"
                          value={slot.maxCapacity}
                          onChange={(e) => {
                            const updatedSlots = [...(formData.passTicketSettings?.slotManagement?.timeSlots || [])];
                            updatedSlots[index] = { ...slot, maxCapacity: parseInt(e.target.value) || 50 };
                            setFormData({
                              ...formData,
                              passTicketSettings: {
                                ...formData.passTicketSettings,
                                slotManagement: {
                                  ...formData.passTicketSettings?.slotManagement,
                                  timeSlots: updatedSlots
                                }
                              }
                            });
                          }}
                          min="1"
                          style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updatedSlots = (formData.passTicketSettings?.slotManagement?.timeSlots || []).filter((_, i) => i !== index);
                            setFormData({
                              ...formData,
                              passTicketSettings: {
                                ...formData.passTicketSettings,
                                slotManagement: {
                                  ...formData.passTicketSettings?.slotManagement,
                                  timeSlots: updatedSlots
                                }
                              }
                            });
                          }}
                          style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', padding: '10px 12px', fontSize: '14px', cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newSlot = { startTime: '09:00', endTime: '12:00', maxCapacity: 50, currentBookings: 0 };
                        setFormData({
                          ...formData,
                          passTicketSettings: {
                            ...formData.passTicketSettings,
                            slotManagement: {
                              ...formData.passTicketSettings?.slotManagement,
                              timeSlots: [...(formData.passTicketSettings?.slotManagement?.timeSlots || []), newSlot]
                            }
                          }
                        });
                      }}
                      style={{ background: '#10b981', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      + Add Slot
                    </button>
                  </div>
                )}
              </div>
              
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>Additional Information Fields</label>
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                  {(formData.passTicketSettings?.additionalFields || []).map((field, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px 60px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Field name (e.g., Father's Name)"
                        value={field.fieldName}
                        onChange={(e) => {
                          const updatedFields = [...(formData.passTicketSettings?.additionalFields || [])];
                          updatedFields[index] = { ...field, fieldName: e.target.value };
                          setFormData({
                            ...formData,
                            passTicketSettings: {
                              ...formData.passTicketSettings,
                              additionalFields: updatedFields
                            }
                          });
                        }}
                        style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                      />
                      <select
                        value={field.fieldType}
                        onChange={(e) => {
                          const updatedFields = [...(formData.passTicketSettings?.additionalFields || [])];
                          updatedFields[index] = { ...field, fieldType: e.target.value };
                          setFormData({
                            ...formData,
                            passTicketSettings: {
                              ...formData.passTicketSettings,
                              additionalFields: updatedFields
                            }
                          });
                        }}
                        style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                      >
                        <option value="text">Text</option>
                        <option value="date">Date</option>
                        <option value="select">Select</option>
                        <option value="phone">Phone</option>
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          checked={field.isRequired}
                          onChange={(e) => {
                            const updatedFields = [...(formData.passTicketSettings?.additionalFields || [])];
                            updatedFields[index] = { ...field, isRequired: e.target.checked };
                            setFormData({
                              ...formData,
                              passTicketSettings: {
                                ...formData.passTicketSettings,
                                additionalFields: updatedFields
                              }
                            });
                          }}
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const updatedFields = (formData.passTicketSettings?.additionalFields || []).filter((_, i) => i !== index);
                          setFormData({
                            ...formData,
                            passTicketSettings: {
                              ...formData.passTicketSettings,
                              additionalFields: updatedFields
                            }
                          });
                        }}
                        style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newField = { fieldName: '', fieldType: 'text', isRequired: false, options: [] };
                      setFormData({
                        ...formData,
                        passTicketSettings: {
                          ...formData.passTicketSettings,
                          additionalFields: [...(formData.passTicketSettings?.additionalFields || []), newField]
                        }
                      });
                    }}
                    style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', fontSize: '14px', cursor: 'pointer' }}
                  >
                    + Add Field
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '12px 24px',
                border: '2px solid #e2e8f0',
                background: 'white',
                color: '#4a5568',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: saving ? '#9ca3af' : 'linear-gradient(45deg, #14b8a6, #0d9488)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                minWidth: '120px'
              }}
            >
              {saving ? '⏳ Saving...' : (ticket ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Parking Settings Editor Component
const ParkingSettingsEditor = ({ settings, onChange }) => {
  const vehicleIcons = {
    car: '🚗',
    motorcycle: '🏍️',
    bike: '🏍️',
    bus: '🚌',
    truck: '🚛'
  };

  const updateSetting = (vehicleType, field, value) => {
    const updatedSettings = {
      ...settings,
      [vehicleType]: {
        ...settings[vehicleType],
        [field]: field === 'price' || field === 'duration' ? parseFloat(value) || 0 : value
      }
    };
    onChange(updatedSettings);
  };

  return (
    <div style={{ marginBottom: '24px', padding: '20px', background: '#eff6ff', borderRadius: '12px', border: '2px solid #3b82f6' }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}>
        🅿️ Parking Settings
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {Object.entries(settings).map(([vehicleType, config]) => (
          <div key={vehicleType} style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>{vehicleIcons[vehicleType] || '🚗'}</span>
              <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>{vehicleType}</span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => updateSetting(vehicleType, 'enabled', e.target.checked)}
                style={{ transform: 'scale(1.2)' }}
              />
              <label style={{ fontSize: '12px', color: '#6b7280' }}>Available</label>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Price (₹)</label>
              <input
                type="number"
                value={config.price}
                onChange={(e) => updateSetting(vehicleType, 'price', e.target.value)}
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Duration (hours)</label>
              <input
                type="number"
                value={config.duration}
                onChange={(e) => updateSetting(vehicleType, 'duration', e.target.value)}
                min="1"
                max="168"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Function Hall Editor Component
const FunctionHallEditor = ({ settings = {}, onChange }) => {
  const [newAmenityName, setNewAmenityName] = useState('');
  const [newAmenityPrice, setNewAmenityPrice] = useState('');
  
  const defaultAmenities = {
    'AC': { enabled: true, price: 500 },
    'Sound System': { enabled: true, price: 300 },
    'Lighting': { enabled: true, price: 200 },
    'Stage': { enabled: true, price: 400 },
    'Seating': { enabled: true, price: 150 }
  };
  
  const defaultSettings = {
    hallCapacity: settings.hallCapacity || 100,
    duration: settings.duration || 4,
    amenities: settings.amenities || defaultAmenities,
    cateringAllowed: settings.cateringAllowed !== undefined ? settings.cateringAllowed : true,
    alcoholAllowed: settings.alcoholAllowed !== undefined ? settings.alcoholAllowed : false,
    parkingIncluded: settings.parkingIncluded !== undefined ? settings.parkingIncluded : true
  };

  const updateSetting = (field, value) => {
    const updatedSettings = { ...defaultSettings, [field]: value };
    onChange(updatedSettings);
  };

  const addAmenity = () => {
    if (newAmenityName.trim() && newAmenityPrice) {
      const updatedAmenities = {
        ...defaultSettings.amenities,
        [newAmenityName.trim()]: {
          enabled: true,
          price: parseFloat(newAmenityPrice) || 0
        }
      };
      const updatedSettings = { ...defaultSettings, amenities: updatedAmenities };
      onChange(updatedSettings);
      setNewAmenityName('');
      setNewAmenityPrice('');
    }
  };

  const removeAmenity = (amenityName) => {
    const updatedAmenities = { ...defaultSettings.amenities };
    delete updatedAmenities[amenityName];
    const updatedSettings = { ...defaultSettings, amenities: updatedAmenities };
    onChange(updatedSettings);
  };

  const updateAmenity = (amenityName, field, value) => {
    const updatedAmenities = {
      ...defaultSettings.amenities,
      [amenityName]: {
        ...defaultSettings.amenities[amenityName],
        [field]: field === 'price' ? parseFloat(value) || 0 : value
      }
    };
    const updatedSettings = { ...defaultSettings, amenities: updatedAmenities };
    onChange(updatedSettings);
  };

  return (
    <div style={{ marginBottom: '24px', padding: '20px', background: '#fef3c7', borderRadius: '12px', border: '2px solid #f59e0b' }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
        🎉 Function Hall Configuration
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Hall Capacity</label>
          <input
            type="number"
            value={defaultSettings.hallCapacity}
            onChange={(e) => updateSetting('hallCapacity', parseInt(e.target.value) || 100)}
            min="1"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>Duration (hours)</label>
          <input
            type="number"
            value={defaultSettings.duration}
            onChange={(e) => updateSetting('duration', parseInt(e.target.value) || 4)}
            min="1"
            max="24"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>
      </div>
      
      {/* Existing Amenities */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        {Object.entries(defaultSettings.amenities).map(([amenityName, config]) => (
          <div key={amenityName} style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            position: 'relative'
          }}>
            <button
              type="button"
              onClick={() => removeAmenity(amenityName)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ×
            </button>
            
            <div style={{ marginBottom: '12px' }}>
              <input
                type="text"
                value={amenityName}
                onChange={(e) => {
                  if (e.target.value !== amenityName) {
                    const updatedAmenities = { ...defaultSettings.amenities };
                    delete updatedAmenities[amenityName];
                    updatedAmenities[e.target.value] = config;
                    onChange({ ...defaultSettings, amenities: updatedAmenities });
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
                placeholder="Amenity Name"
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => updateAmenity(amenityName, 'enabled', e.target.checked)}
                style={{ transform: 'scale(1.2)' }}
              />
              <label style={{ fontSize: '12px', color: '#6b7280' }}>Enabled</label>
            </div>
            
            <input
              type="number"
              value={config.price}
              onChange={(e) => updateAmenity(amenityName, 'price', e.target.value)}
              min="0"
              step="0.01"
              placeholder="Price"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        ))}
      </div>
      
      {/* Add New Amenity */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        border: '2px dashed #d1d5db',
        marginBottom: '16px'
      }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px' }}>Add New Amenity</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px', gap: '8px', alignItems: 'end' }}>
          <input
            type="text"
            value={newAmenityName}
            onChange={(e) => setNewAmenityName(e.target.value)}
            placeholder="Amenity name (e.g., Projector)"
            style={{
              padding: '8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          <input
            type="number"
            value={newAmenityPrice}
            onChange={(e) => setNewAmenityPrice(e.target.value)}
            placeholder="Price"
            min="0"
            step="0.01"
            style={{
              padding: '8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          <button
            type="button"
            onClick={addAmenity}
            disabled={!newAmenityName.trim() || !newAmenityPrice}
            style={{
              background: newAmenityName.trim() && newAmenityPrice ? '#10b981' : '#9ca3af',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: newAmenityName.trim() && newAmenityPrice ? 'pointer' : 'not-allowed'
            }}
          >
            Add
          </button>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={defaultSettings.cateringAllowed}
            onChange={(e) => updateSetting('cateringAllowed', e.target.checked)}
            style={{ transform: 'scale(1.2)' }}
          />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Catering Allowed</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={defaultSettings.alcoholAllowed}
            onChange={(e) => updateSetting('alcoholAllowed', e.target.checked)}
            style={{ transform: 'scale(1.2)' }}
          />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Alcohol Allowed</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={defaultSettings.parkingIncluded}
            onChange={(e) => updateSetting('parkingIncluded', e.target.checked)}
            style={{ transform: 'scale(1.2)' }}
          />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Parking Included</span>
        </label>
      </div>
    </div>
  );
};

// Puja Services Editor Component
const PujaServicesEditor = ({ services, onChange }) => {
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');

  const addService = () => {
    if (newServiceName.trim() && newServicePrice) {
      const updatedServices = {
        ...services,
        [newServiceName.trim()]: {
          enabled: true,
          price: parseFloat(newServicePrice) || 0
        }
      };
      onChange(updatedServices);
      setNewServiceName('');
      setNewServicePrice('');
    }
  };

  const removeService = (serviceName) => {
    const updatedServices = { ...services };
    delete updatedServices[serviceName];
    onChange(updatedServices);
  };

  const updateService = (serviceName, field, value) => {
    const updatedServices = {
      ...services,
      [serviceName]: {
        ...services[serviceName],
        [field]: field === 'price' ? parseFloat(value) || 0 : value
      }
    };
    onChange(updatedServices);
  };

  return (
    <div style={{ marginBottom: '24px', padding: '20px', background: '#fef7e0', borderRadius: '12px', border: '2px solid #f6cc02' }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
        🙏 Puja Services Configuration
      </h3>
      
      {/* Existing Services */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        {Object.entries(services).map(([serviceName, config]) => (
          <div key={serviceName} style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            position: 'relative'
          }}>
            <button
              type="button"
              onClick={() => removeService(serviceName)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ×
            </button>
            
            <div style={{ marginBottom: '12px' }}>
              <input
                type="text"
                value={serviceName}
                onChange={(e) => {
                  if (e.target.value !== serviceName) {
                    const updatedServices = { ...services };
                    delete updatedServices[serviceName];
                    updatedServices[e.target.value] = config;
                    onChange(updatedServices);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
                placeholder="Service Name"
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => updateService(serviceName, 'enabled', e.target.checked)}
                style={{ transform: 'scale(1.2)' }}
              />
              <label style={{ fontSize: '12px', color: '#6b7280' }}>Enabled</label>
            </div>
            
            <input
              type="number"
              value={config.price}
              onChange={(e) => updateService(serviceName, 'price', e.target.value)}
              min="0"
              step="0.01"
              placeholder="Price"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        ))}
      </div>
      
      {/* Add New Service */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        border: '2px dashed #d1d5db'
      }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px' }}>Add New Service</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 80px', gap: '8px', alignItems: 'end' }}>
          <input
            type="text"
            value={newServiceName}
            onChange={(e) => setNewServiceName(e.target.value)}
            placeholder="Service name (e.g., Morning Aarti)"
            style={{
              padding: '8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          <input
            type="number"
            value={newServicePrice}
            onChange={(e) => setNewServicePrice(e.target.value)}
            placeholder="Price"
            min="0"
            step="0.01"
            style={{
              padding: '8px',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          <button
            type="button"
            onClick={addService}
            disabled={!newServiceName.trim() || !newServicePrice}
            style={{
              background: newServiceName.trim() && newServicePrice ? '#10b981' : '#9ca3af',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: newServiceName.trim() && newServicePrice ? 'pointer' : 'not-allowed'
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantTickets;